import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/index.ts";
import { DataTable, type DataColumn } from "../../auth/DataTable.tsx";
import { Drawer } from "../../auth/Drawer.tsx";
import { Modal } from "../../auth/Modal.tsx";
import { StatusPill } from "../../auth/Pills.tsx";
import { ApiError } from "../../lib/api.ts";
import {
  checkTeamFieldAvailability,
  createTeam,
  deleteTeam,
  isMfaRequiredError,
  listTeams,
  updateTeam,
  TEAMS_KEY,
  type Team,
  type TeamUniqueField,
} from "../../lib/management-client.ts";
import { validateEmail, validateOptionalText, validateRequiredText, formatWebsiteSmart } from "../../lib/validate.ts";
import { OtpInput } from "../../components/OtpInput.tsx";
import "./account.css";

/**
 * The SUPERADMIN "Teams" screen (teams = tenants = customers/organizations). Visible only to a
 * superadmin (an admin of the root team); the shell hides the nav entry and this screen refuses a
 * non-superadmin. A superadmin can:
 *   - see EVERY team (the backend scopes nothing here — superadmins hold an admin membership in each
 *     team, so this list is the authority on what exists), and
 *   - create a team with its organization details and a MANDATORY owner (a team can never be created
 *     without an admin). The owner is invited as the team's first admin; the superadmin is also
 *     granted admin in the new team so they can switch into and manage it from the team switcher.
 *
 * Team OWNERS and members never reach this screen; they manage their own team from the Team screen.
 */

function friendlyError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.kind === "forbidden") return "Only a superadmin can manage teams.";
    if (error.kind === "waking") return "The control plane is still waking up. Try again in a moment.";
    if (error.kind === "network") return "We could not reach the control plane. Check your connection.";
  }
  return fallback;
}

export function TeamsScreen(): ReactNode {
  const auth = useAuth();
  if (auth.identity !== null && !auth.identity.isSuperadmin) {
    return (
      <section className="oa-auth oa-account" aria-label="Teams">
        <header className="oa-account__header">
          <h1 className="oa-account__title">Teams</h1>
        </header>
        <div className="oa-account__card" data-kind="restricted">
          <h2 className="oa-account__card-title">Not available for your role</h2>
          <p className="oa-account__card-note">
            Only a superadmin can create or view all teams. Manage your own team from the Team screen.
          </p>
        </div>
      </section>
    );
  }
  return <TeamsView />;
}

function TeamsView(): ReactNode {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [banner, setBanner] = useState<string | null>(null);
  // The team whose edit drawer is open, or null when the drawer is closed.
  const [selected, setSelected] = useState<Team | null>(null);

  const teamsQuery = useQuery({
    queryKey: TEAMS_KEY,
    queryFn: ({ signal }) => listTeams(signal),
    staleTime: 15_000,
    retry: 1,
  });

  const columns: readonly DataColumn<Team>[] = [
    {
      key: "name",
      header: "Team",
      isRowHeader: true,
      cell: (t) => (
        <span className="oa-account__member">
          <span className="oa-account__member-name">
            {t.name} {t.isRoot && <StatusPill tone="accent">root</StatusPill>}
          </span>
          {t.orgLegalName !== null && <span className="oa-account__member-email">{t.orgLegalName}</span>}
        </span>
      ),
    },
    { key: "domain", header: "Domain", cell: (t) => t.orgDomain ?? "n/a" },
    { key: "members", header: "Members", cell: (t) => String(t.memberCount) },
    { key: "id", header: "Team id", cell: (t) => <code className="oa-account__ref">{t.id}</code> },
  ];

  return (
    <section className="oa-auth oa-account" aria-label="Teams">
      <header className="oa-account__header">
        <h1 className="oa-account__title">Teams</h1>
        <p className="oa-account__subtitle">
          Every team (customer / organization) on the control plane. Create a team with its owner,
          then switch into it from the team selector to manage it.
        </p>
      </header>

      {banner !== null && (
        <p className="oa-account__feedback" data-kind="ok" role="status" aria-live="polite">
          {banner}
        </p>
      )}

      <CreateTeamCard
        onCreated={async (name) => {
          setBanner(`Team "${name}" created and its owner invited.`);
          await queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
          void auth.refresh();
        }}
      />

      <article className="oa-account__card" aria-labelledby="oa-teams-list">
        <h2 className="oa-account__card-title" id="oa-teams-list">
          All teams
        </h2>
        {teamsQuery.isPending ? (
          <div className="oa-account__state" role="status" aria-live="polite">
            Loading teams…
          </div>
        ) : teamsQuery.isError ? (
          <div className="oa-account__feedback" data-kind="error" role="alert">
            {friendlyError(teamsQuery.error, "Could not load teams.")}
          </div>
        ) : (
          <DataTable
            caption="All teams"
            columns={columns}
            rows={teamsQuery.data ?? []}
            rowKey={(t) => t.id}
            emptyMessage="No teams yet."
            onRowActivate={(t) => {
              setBanner(null);
              setSelected(t);
            }}
            rowLabel={(t) => `Edit ${t.name}`}
          />
        )}
      </article>

      <EditTeamDrawer
        team={selected}
        onClose={() => setSelected(null)}
        onSaved={async (name) => {
          setSelected(null);
          setBanner(`Team "${name}" updated.`);
          await queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
          void auth.refresh();
        }}
        onDeleted={async (name) => {
          setSelected(null);
          setBanner(`Team "${name}" deleted.`);
          await queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
          // The superadmin's admin membership in that team is gone; re-derive the session so the
          // team switcher and any team-scoped state drop it.
          void auth.refresh();
        }}
      />
    </section>
  );
}


function CreateTeamCard(props: { onCreated: (name: string) => void | Promise<void> }): ReactNode {
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [orgLegalName, setOrgLegalName] = useState("");
  const [orgDomain, setOrgDomain] = useState("");
  const [orgContactEmail, setOrgContactEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step-up MFA: when the server challenges a privileged create (the superadmin has TOTP enrolled),
  // we reveal a 6-digit code field and retry the SAME create with the fresh code. The entered values
  // are kept, so the operator only adds the code and submits again.
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState<string | null>(null);

  // Per-field error strings (format OR uniqueness), shown under each input. `null` = no error.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  // Fields with an availability check currently in flight (to show a "checking…" hint + disable submit).
  const [checking, setChecking] = useState<Record<string, boolean>>({});
  // A monotonic token per field so a slow earlier check cannot overwrite a newer one (race guard).
  const checkSeq = useRef<Record<string, number>>({});

  const nameId = useId();
  const ownerId = useId();
  const legalId = useId();
  const domainId = useId();
  const contactId = useId();
  const mfaId = useId();

  const setFieldError = useCallback((field: string, message: string | null): void => {
    setFieldErrors((prev) => ({ ...prev, [field]: message }));
  }, []);

  /** The synchronous (format) validator for a field; mirrors the server's shape checks. */
  const formatError = useCallback(
    (field: string, value: string): string | null => {
      switch (field) {
        case "name":
          return validateRequiredText(value, "Team name", 128);
        case "ownerEmail":
          return validateEmail(value, "Team owner email", true);
        case "orgLegalName":
          return validateOptionalText(value, "Organization legal name", 256);
        case "orgDomain":
          return validateOptionalText(value, "Organization domain", 256);
        case "orgContactEmail":
          return validateEmail(value, "Organization contact email");
        default:
          return null;
      }
    },
    [],
  );

  /**
   * Validate a field as the operator leaves it (onBlur): first the format check, then — for the four
   * UNIQUE fields with a non-empty, well-formed value — an async availability check against the
   * server. A race guard (checkSeq) ensures only the latest check for a field wins.
   */
  const validateField = useCallback(
    async (field: string, value: string): Promise<void> => {
      const fmt = formatError(field, value);
      if (fmt !== null) {
        setFieldError(field, fmt);
        return;
      }
      setFieldError(field, null);

      const uniqueFields: readonly TeamUniqueField[] = ["name", "ownerEmail", "orgLegalName", "orgDomain"];
      if (!(uniqueFields as readonly string[]).includes(field) || value.trim() === "") return;

      const seq = (checkSeq.current[field] ?? 0) + 1;
      checkSeq.current[field] = seq;
      setChecking((prev) => ({ ...prev, [field]: true }));
      try {
        const available = await checkTeamFieldAvailability(field as TeamUniqueField, value.trim());
        if (checkSeq.current[field] !== seq) return; // a newer check superseded this one
        if (!available) {
          const label: Record<string, string> = {
            name: "A team with this name already exists.",
            ownerEmail: "This email already owns (or was invited to own) a team.",
            orgLegalName: "An organization with this legal name already exists.",
            orgDomain: "An organization with this domain already exists.",
          };
          setFieldError(field, label[field] ?? "This value is already taken.");
        }
      } catch {
        // A transport error must not block the operator; the server re-checks on submit anyway.
        if (checkSeq.current[field] === seq) setFieldError(field, null);
      } finally {
        if (checkSeq.current[field] === seq) {
          setChecking((prev) => ({ ...prev, [field]: false }));
        }
      }
    },
    [formatError, setFieldError],
  );

  const anyFieldError = Object.values(fieldErrors).some((e) => e !== null && e !== undefined);
  const anyChecking = Object.values(checking).some((c) => c === true);
  // The required fields must be present (format-valid) before submit is enabled.
  const requiredMissing = formatError("name", name) !== null || formatError("ownerEmail", ownerEmail) !== null;
  // When step-up MFA has been requested, the submit also needs a 6-digit code.
  const mfaCodeIncomplete = mfaRequired && !/^\d{6}$/.test(mfaCode.trim());
  const submitDisabled = busy || anyChecking || anyFieldError || requiredMissing || mfaCodeIncomplete;

  /**
   * Perform the actual create, optionally carrying a step-up TOTP `code`. Shared by the first submit
   * and the MFA retry so the two paths behave identically. On an {@link isMfaRequiredError} it reveals
   * the code field (first attempt) or reports a bad code (retry) and leaves the form intact so the
   * operator can enter/correct the code and submit again.
   */
  const performCreate = useCallback(
    async (code?: string): Promise<void> => {
      setBusy(true);
      try {
        await createTeam(
          {
            name: name.trim(),
            ownerEmail: ownerEmail.trim(),
            ...(orgLegalName.trim() !== "" ? { orgLegalName: orgLegalName.trim() } : {}),
            ...(orgDomain.trim() !== "" ? { orgDomain: orgDomain.trim() } : {}),
            ...(orgContactEmail.trim() !== "" ? { orgContactEmail: orgContactEmail.trim() } : {}),
          },
          code,
        );
        const created = name.trim();
        setName("");
        setOwnerEmail("");
        setOrgLegalName("");
        setOrgDomain("");
        setOrgContactEmail("");
        setFieldErrors({});
        setMfaRequired(false);
        setMfaCode("");
        setMfaError(null);
        await props.onCreated(created);
      } catch (e) {
        if (isMfaRequiredError(e)) {
          // The superadmin has TOTP enrolled; this privileged create needs a fresh code. If we had
          // already supplied one, it was wrong/expired — say so; otherwise reveal the field.
          setMfaRequired(true);
          setMfaError(code !== undefined && code !== "" ? "That code was not accepted. Enter a fresh 6-digit code." : null);
          if (code === undefined || code === "") {
            setError(null);
          }
          return;
        }
        // A 409 is a uniqueness conflict. `apiFetch` surfaces the status + the server message; it does
        // not carry a structured field, so we show the server's message AND re-run the per-field
        // availability checks so the offending input(s) get highlighted inline.
        if (e instanceof ApiError && e.status === 409) {
          setError(e.message || "That value is already taken. See the highlighted field(s).");
          await Promise.all([
            validateField("name", name),
            validateField("ownerEmail", ownerEmail),
            validateField("orgLegalName", orgLegalName),
            validateField("orgDomain", orgDomain),
          ]);
        } else {
          setError(friendlyError(e, "Could not create the team."));
        }
      } finally {
        setBusy(false);
      }
    },
    [name, ownerEmail, orgLegalName, orgDomain, orgContactEmail, validateField, props],
  );

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);
      // Final client-side gate: re-run the format checks for every field.
      const fields: ReadonlyArray<[string, string]> = [
        ["name", name],
        ["ownerEmail", ownerEmail],
        ["orgLegalName", orgLegalName],
        ["orgDomain", orgDomain],
        ["orgContactEmail", orgContactEmail],
      ];
      let hadError = false;
      for (const [f, v] of fields) {
        const e = formatError(f, v);
        setFieldError(f, e);
        if (e !== null) hadError = true;
      }
      if (hadError || anyFieldError) {
        setError("Please fix the highlighted fields before creating the team.");
        return;
      }
      // If the server has already asked for a code, carry it on this submit.
      await performCreate(mfaRequired && mfaCode.trim() !== "" ? mfaCode.trim() : undefined);
    },
    [name, ownerEmail, orgLegalName, orgDomain, orgContactEmail, anyFieldError, formatError, setFieldError, performCreate, mfaRequired, mfaCode],
  );

  function fieldRow(
    field: string,
    id: string,
    label: string,
    type: "text" | "email",
    value: string,
    onChange: (v: string) => void,
    placeholder?: string,
  ): ReactNode {
    const err = fieldErrors[field] ?? null;
    const isChecking = checking[field] === true;
    return (
      <div className="oa-account__row">
        <label className="oa-account__label" htmlFor={id}>
          {label}
        </label>
        <input
          id={id}
          className="oa-account__input"
          type={type}
          autoComplete="off"
          value={value}
          {...(placeholder !== undefined ? { placeholder } : {})}
          aria-invalid={err ? "true" : undefined}
          onChange={(e) => {
            onChange(e.target.value);
            // Clear a stale error as the operator edits; re-validated on blur.
            if (err !== null) setFieldError(field, null);
          }}
          onBlur={(e) => void validateField(field, e.target.value)}
        />
        {isChecking && <span className="oa-account__field-hint">Checking availability…</span>}
        {err && (
          <span className="oa-account__field-error" role="alert">
            {err}
          </span>
        )}
      </div>
    );
  }

  return (
    <article className="oa-account__card" aria-labelledby="oa-teams-create">
      <h2 className="oa-account__card-title" id="oa-teams-create">
        Create a team
      </h2>
      <p className="oa-account__card-note">
        A team is a customer or organization. A team owner (admin) is required; they will be invited
        to accept and set a password, and can then add members to their team. Each team name, owner
        email, organization legal name and organization domain must be unique.
      </p>
      <form className="oa-account__form" onSubmit={submit}>
        {fieldRow("name", nameId, "Team name (required)", "text", name, setName)}
        {fieldRow("ownerEmail", ownerId, "Team owner email (required)", "email", ownerEmail, setOwnerEmail)}
        {fieldRow("orgLegalName", legalId, "Organization legal name", "text", orgLegalName, setOrgLegalName)}
        {fieldRow("orgDomain", domainId, "Organization domain", "text", orgDomain, (v) => setOrgDomain(formatWebsiteSmart(v)), "https://example.com")}
        {fieldRow("orgContactEmail", contactId, "Organization contact email", "email", orgContactEmail, setOrgContactEmail)}
        {error !== null && (
          <p className="oa-account__feedback" data-kind="error" role="alert">
            {error}
          </p>
        )}
        {mfaRequired && (
          <div className="oa-account__row">
            <label className="oa-account__label" htmlFor={mfaId}>
              Authentication code (required)
            </label>
            <p className="oa-account__card-note">
              Creating a team is a protected action. Enter the current 6-digit code from your
              authenticator app to continue.
            </p>
            <OtpInput
              id={mfaId}
              value={mfaCode}
              invalid={mfaError !== null}
              autoFocus
              align="start"
              onChange={(v) => {
                setMfaCode(v);
                if (mfaError !== null) setMfaError(null);
              }}
            />
            {mfaError !== null && (
              <span className="oa-account__field-error" role="alert">
                {mfaError}
              </span>
            )}
          </div>
        )}
        <div className="oa-account__btn-row">
          <button type="submit" className="oa-account__btn" disabled={submitDisabled}>
            {busy ? "Creating…" : mfaRequired ? "Verify & create team" : "Create team"}
          </button>
        </div>
      </form>
    </article>
  );
}

// -------------------------------------------------------------------------------------------------
// EditTeamDrawer — edit a team's editable fields (name / org legal name / domain / contact email).
// Owner email is NOT editable (it is the first-admin membership, a separate deferred concern); the
// list does not carry it, so it is simply not shown. DELETE / DEACTIVATE are deliberately out of
// scope here. The root team is editable too (name/domain are legitimate); nothing here changes root
// status.
// -------------------------------------------------------------------------------------------------

/** The four editable fields, keyed as the client/server expect. */
type EditField = "name" | "orgLegalName" | "orgDomain" | "orgContactEmail";

function EditTeamDrawer(props: {
  team: Team | null;
  onClose: () => void;
  onSaved: (name: string) => void | Promise<void>;
  onDeleted: (name: string) => void | Promise<void>;
}): ReactNode {
  const team = props.team;

  const [name, setName] = useState("");
  const [orgLegalName, setOrgLegalName] = useState("");
  const [orgDomain, setOrgDomain] = useState("");
  const [orgContactEmail, setOrgContactEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Delete flow (DANGER): a confirm dialog guards an IRREVERSIBLE scoped delete. `confirmDelete`
  // toggles the alertdialog; `deleting` disables its buttons; `deleteError` shows the server's
  // refusal (e.g. a 409 "team not empty") INLINE in the dialog so it stays open and the operator
  // learns the team must be emptied first.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The seeded (current) values, so an unchanged unique field skips the availability check (the
  // server now excludes the edited team, so an unchanged value must never show a false "taken").
  const [seeded, setSeeded] = useState<{ name: string; orgLegalName: string; orgDomain: string; orgContactEmail: string } | null>(null);

  // Per-field error strings (format OR uniqueness). `null` = no error.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  // Fields with an availability check in flight (to show a hint + disable submit).
  const [checking, setChecking] = useState<Record<string, boolean>>({});
  // A monotonic token per field so a slow earlier check cannot overwrite a newer one (race guard).
  const checkSeq = useRef<Record<string, number>>({});

  const nameId = useId();
  const legalId = useId();
  const domainId = useId();
  const contactId = useId();

  // Seed the form (and reset validation) when a different team's drawer opens — no layout effect,
  // mirroring UserAccessDrawer's `seededFor` pattern.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (team !== null && seededFor !== team.id) {
    setSeededFor(team.id);
    const s = {
      name: team.name,
      orgLegalName: team.orgLegalName ?? "",
      orgDomain: team.orgDomain ?? "",
      orgContactEmail: team.orgContactEmail ?? "",
    };
    setName(s.name);
    setOrgLegalName(s.orgLegalName);
    setOrgDomain(s.orgDomain);
    setOrgContactEmail(s.orgContactEmail);
    setSeeded(s);
    setFieldErrors({});
    setChecking({});
    setError(null);
    checkSeq.current = {};
    setConfirmDelete(false);
    setDeleting(false);
    setDeleteError(null);
  }

  const setFieldError = useCallback((field: string, message: string | null): void => {
    setFieldErrors((prev) => ({ ...prev, [field]: message }));
  }, []);

  /** The synchronous (format) validator for a field; mirrors the server's shape checks. */
  const formatError = useCallback((field: EditField, value: string): string | null => {
    switch (field) {
      case "name":
        return validateRequiredText(value, "Team name", 128);
      case "orgLegalName":
        return validateOptionalText(value, "Organization legal name", 256);
      case "orgDomain":
        return validateOptionalText(value, "Organization domain", 256);
      case "orgContactEmail":
        return validateEmail(value, "Organization contact email");
    }
  }, []);

  /**
   * Validate a field on blur: format first, then (for the three UNIQUE fields with a non-empty,
   * well-formed value that CHANGED from the seeded value) an availability check. An unchanged value
   * skips the check — the server excludes the edited team, so an unchanged value is never a
   * self-conflict. A race guard (checkSeq) ensures only the latest check for a field wins.
   */
  const validateField = useCallback(
    async (field: EditField, value: string): Promise<void> => {
      const fmt = formatError(field, value);
      if (fmt !== null) {
        setFieldError(field, fmt);
        return;
      }
      setFieldError(field, null);

      const uniqueFields: readonly EditField[] = ["name", "orgLegalName", "orgDomain"];
      if (!uniqueFields.includes(field) || value.trim() === "") return;
      // Unchanged from the current value → treat as available (no server call).
      const current = seeded?.[field] ?? "";
      if (value.trim() === current.trim()) return;

      const seq = (checkSeq.current[field] ?? 0) + 1;
      checkSeq.current[field] = seq;
      setChecking((prev) => ({ ...prev, [field]: true }));
      try {
        const available = await checkTeamFieldAvailability(field as TeamUniqueField, value.trim());
        if (checkSeq.current[field] !== seq) return; // a newer check superseded this one
        if (!available) {
          const label: Record<string, string> = {
            name: "A team with this name already exists.",
            orgLegalName: "An organization with this legal name already exists.",
            orgDomain: "An organization with this domain already exists.",
          };
          setFieldError(field, label[field] ?? "This value is already taken.");
        }
      } catch {
        // A transport error must not block the operator; the server re-checks on save anyway.
        if (checkSeq.current[field] === seq) setFieldError(field, null);
      } finally {
        if (checkSeq.current[field] === seq) {
          setChecking((prev) => ({ ...prev, [field]: false }));
        }
      }
    },
    [formatError, setFieldError, seeded],
  );

  const anyFieldError = Object.values(fieldErrors).some((e) => e !== null && e !== undefined);
  const anyChecking = Object.values(checking).some((c) => c === true);
  const requiredMissing = formatError("name", name) !== null;
  const submitDisabled = busy || anyChecking || anyFieldError || requiredMissing;

  const save = useCallback(async (): Promise<void> => {
    if (team === null) return;
    setError(null);
    // Final client-side gate: re-run the format checks for every field.
    const fields: ReadonlyArray<[EditField, string]> = [
      ["name", name],
      ["orgLegalName", orgLegalName],
      ["orgDomain", orgDomain],
      ["orgContactEmail", orgContactEmail],
    ];
    let hadError = false;
    for (const [f, v] of fields) {
      const e = formatError(f, v);
      setFieldError(f, e);
      if (e !== null) hadError = true;
    }
    if (hadError) {
      setError("Please fix the highlighted fields before saving.");
      return;
    }

    setBusy(true);
    try {
      await updateTeam(team.id, {
        name: name.trim(),
        orgLegalName: orgLegalName.trim(),
        orgDomain: orgDomain.trim(),
        orgContactEmail: orgContactEmail.trim(),
      });
      await props.onSaved(name.trim());
    } catch (e) {
      // A 409 is a uniqueness conflict. `apiFetch` surfaces the status + server message (no
      // structured field), so show the message AND re-run the per-field availability checks so the
      // offending input(s) highlight inline (mirroring create).
      if (e instanceof ApiError && e.status === 409) {
        setError(e.message || "That value is already taken. See the highlighted field(s).");
        await Promise.all([
          validateField("name", name),
          validateField("orgLegalName", orgLegalName),
          validateField("orgDomain", orgDomain),
        ]);
      } else if (e instanceof ApiError && e.status === 400) {
        setError(e.message || "Some values are not valid. Please check the fields.");
      } else {
        setError(friendlyError(e, "Could not update the team."));
      }
    } finally {
      setBusy(false);
    }
  }, [team, name, orgLegalName, orgDomain, orgContactEmail, formatError, setFieldError, validateField, props]);

  /**
   * Perform the IRREVERSIBLE scoped delete after the operator confirms. On success: close the
   * confirm + drawer and let the parent show a banner + refresh. On a 409 (`not_empty`) show the
   * server's message INLINE in the dialog and keep it open so the operator learns the team must be
   * emptied first; on 403 (root — defense-in-depth, the button should not render) or any other
   * failure show a friendly inline error.
   */
  const performDelete = useCallback(async (): Promise<void> => {
    if (team === null) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteTeam(team.id);
      const deleted = team.name;
      setConfirmDelete(false);
      await props.onDeleted(deleted);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setDeleteError(e.message || "This team still has data and must be emptied before it can be deleted.");
      } else if (e instanceof ApiError && e.status === 403) {
        setDeleteError(e.message || "This team cannot be deleted.");
      } else {
        setDeleteError(friendlyError(e, "Could not delete the team."));
      }
    } finally {
      setDeleting(false);
    }
  }, [team, props]);

  function fieldRow(
    field: EditField,
    id: string,
    label: string,
    type: "text" | "email",
    value: string,
    onChange: (v: string) => void,
    placeholder?: string,
  ): ReactNode {
    const err = fieldErrors[field] ?? null;
    const isChecking = checking[field] === true;
    return (
      <div className="oa-account__row">
        <label className="oa-account__label" htmlFor={id}>
          {label}
        </label>
        <input
          id={id}
          className="oa-account__input"
          type={type}
          autoComplete="off"
          value={value}
          {...(placeholder !== undefined ? { placeholder } : {})}
          aria-invalid={err ? "true" : undefined}
          onChange={(e) => {
            onChange(e.target.value);
            if (err !== null) setFieldError(field, null);
          }}
          onBlur={(e) => void validateField(field, e.target.value)}
        />
        {isChecking && <span className="oa-account__field-hint">Checking availability…</span>}
        {err && (
          <span className="oa-account__field-error" role="alert">
            {err}
          </span>
        )}
      </div>
    );
  }

  return (
    <Drawer
      open={team !== null}
      onClose={props.onClose}
      title="Edit team"
      footer={
        team === null ? null : (
          <>
            <button type="button" className="oa-account__btn oa-account__btn--ghost" disabled={busy} onClick={props.onClose}>
              Cancel
            </button>
            <button type="button" className="oa-account__btn" disabled={submitDisabled} onClick={() => void save()}>
              {busy ? "Saving…" : "Save changes"}
            </button>
          </>
        )
      }
    >
      {team !== null && (
        <div className="oa-account__drawer-body">
          <dl className="oa-account__facts">
            <div className="oa-account__fact">
              <dt>Team id</dt>
              <dd>
                <code className="oa-account__ref">{team.id}</code> {team.isRoot && <StatusPill tone="accent">root</StatusPill>}
              </dd>
            </div>
            <div className="oa-account__fact">
              <dt>Members</dt>
              <dd>{String(team.memberCount)}</dd>
            </div>
          </dl>
          <p className="oa-account__card-note">
            The team owner is set at creation and is not editable here. Each team name, organization
            legal name and organization domain must be unique.
          </p>
          {fieldRow("name", nameId, "Team name (required)", "text", name, setName)}
          {fieldRow("orgLegalName", legalId, "Organization legal name", "text", orgLegalName, setOrgLegalName)}
          {fieldRow("orgDomain", domainId, "Organization domain", "text", orgDomain, (v) => setOrgDomain(formatWebsiteSmart(v)), "https://example.com")}
          {fieldRow("orgContactEmail", contactId, "Organization contact email", "email", orgContactEmail, setOrgContactEmail)}
          {error !== null && (
            <p className="oa-account__feedback" data-kind="error" role="alert">
              {error}
            </p>
          )}
          {/* DANGER ZONE — only for a NON-ROOT team. The root team never shows this (defense in
              depth: the server also refuses deleting the root team). The delete is scoped + SAFE: it
              refuses a team that still has operational data. */}
          {!team.isRoot && (
            <div className="oa-account__danger" role="group" aria-label="Danger zone">
              <h3 className="oa-account__card-title">Danger zone</h3>
              <p className="oa-account__card-note">
                Permanently delete this team. This cannot be undone. A team that still has
                operational data cannot be deleted until it is emptied.
              </p>
              <div className="oa-account__btn-row">
                <button
                  type="button"
                  className="oa-account__btn oa-account__btn--danger"
                  disabled={busy || deleting}
                  onClick={() => {
                    setDeleteError(null);
                    setConfirmDelete(true);
                  }}
                >
                  Delete team
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <ConfirmDeleteTeamDialog
        open={confirmDelete && team !== null && !team.isRoot}
        teamName={team?.name ?? ""}
        busy={deleting}
        error={deleteError}
        onCancel={() => {
          setConfirmDelete(false);
          setDeleteError(null);
        }}
        onConfirm={() => void performDelete()}
      />
    </Drawer>
  );
}

// -------------------------------------------------------------------------------------------------
// ConfirmDeleteTeamDialog — explicit, accessible confirmation for an IRREVERSIBLE team delete.
// Mirrors TeamMembersScreen's ConfirmBanDialog: a shared Modal with role="alertdialog". A server
// refusal (e.g. a 409 "team not empty", or a 403 for the root team) is shown INLINE and the dialog
// stays open, so the operator learns the team must be emptied first rather than losing the message.
// -------------------------------------------------------------------------------------------------

function ConfirmDeleteTeamDialog(props: {
  open: boolean;
  teamName: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}): ReactNode {
  return (
    <Modal
      open={props.open}
      onClose={props.onCancel}
      role="alertdialog"
      title="Delete team"
      description={`Permanently delete ${props.teamName}? This cannot be undone.`}
      footer={
        <>
          <button
            type="button"
            className="oa-account__btn oa-account__btn--ghost"
            disabled={props.busy}
            onClick={props.onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="oa-account__btn oa-account__btn--danger"
            disabled={props.busy}
            onClick={props.onConfirm}
          >
            {props.busy ? "Deleting…" : "Delete team"}
          </button>
        </>
      }
    >
      <p className="oa-account__card-note">
        This permanently removes the team and its memberships, invites and sign-in configuration. It
        does not touch any operational data — a team that still has data must be emptied first.
      </p>
      {props.error !== null && (
        <p className="oa-account__feedback" data-kind="error" role="alert">
          {props.error}
        </p>
      )}
    </Modal>
  );
}
